import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import Divider from '@mui/material/Divider';

import { DataTable } from '../components/DataTable';
import { LocationAssignmentForm } from '../components/location-assignments/LocationAssignmentForm';
import { AssignmentStatusChip } from '../components/AssignmentStatusChip';
import { LocationEquivalenciesPanel } from '../components/product-equivalencies/LocationEquivalenciesPanel';
import { locationAssignmentsApi, locationsApi, productsApi, organizationsApi, beaconAssignmentsApi, locationEquivalenciesApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { extractApiError } from '../utils/apiErrors';
import type { LocationAssignment, CreateAssignmentData } from '../api/config';

export const LocationAssignmentsPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedAssignment, setSelectedAssignment] = React.useState<LocationAssignment | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' | 'warning' } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Initialize filters from URL parameters
  const [filters, setFilters] = React.useState({
    location_id: searchParams.get('location_id') || '',
    sku: searchParams.get('sku') || '',
    status: '',
    organization_id: searchParams.get('organization_id') || '',
  });

  const {
    rows: assignments,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['location_assignments', filters],
    queryFn: (f, p) => locationAssignmentsApi.getPage(f, p),
    filters,
    defaultSort: { field: 'created_at', sort: 'desc' },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations', filters.organization_id],
    queryFn: () => filters.organization_id 
      ? locationsApi.getAll({ organization_id: filters.organization_id })
      : locationsApi.getAllWithoutFilters(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  // Fetch assignments for the selected location to determine available products
  const { data: locationAssignments = [] } = useQuery({
    queryKey: ['location_assignments_for_products', filters.location_id],
    queryFn: () => locationAssignmentsApi.getAll({ location_id: filters.location_id }),
    enabled: !!filters.location_id,
  });

  // Compute available products for the dropdown based on selected location
  const availableProducts = React.useMemo(() => {
    if (!filters.location_id) {
      // No location selected - show all products sorted by SKU
      return [...products].sort((a, b) => a.sku.localeCompare(b.sku));
    }
    
    // Get unique SKUs from assignments at this location
    const assignedSkus = new Set(locationAssignments.map(a => a.sku));
    
    // Filter products to only those assigned to this location and sort by SKU
    return products
      .filter(p => assignedSkus.has(p.sku))
      .sort((a, b) => a.sku.localeCompare(b.sku));
  }, [filters.location_id, products, locationAssignments]);

  // Note: onSuccess/onError handled in handleSubmit for atomic transaction behavior
  const createMutation = useMutation({
    mutationFn: locationAssignmentsApi.create,
  });

  // Note: onSuccess/onError handled in handleSubmit for atomic transaction behavior
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LocationAssignment> }) =>
      locationAssignmentsApi.update(id, data),
  });

  const deleteMutation = useMutation({
    mutationFn: locationAssignmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
      setSnackbar({ message: 'Assignment deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete assignment'), severity: 'error' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      locationAssignmentsApi.deactivate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
      setSnackbar({ message: 'Assignment deactivated successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to deactivate assignment'), severity: 'error' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: locationAssignmentsApi.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
      setSnackbar({ message: 'Assignment reactivated successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to reactivate assignment'), severity: 'error' });
    },
  });

  const discontinueMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      locationAssignmentsApi.discontinue(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
      setSnackbar({ message: 'Assignment discontinued successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to discontinue assignment'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { 
      field: 'location_name', 
      headerName: 'Location', 
      width: 200,
      valueGetter: (params: GridRenderCellParams<LocationAssignment>) => {
        return params.row?.location?.name || params.row?.location?.slug || params.row?.location_id || '';
      },
    },
    { 
      field: 'sku', 
      headerName: 'SKU', 
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params: GridRenderCellParams<LocationAssignment>) => {
        const status = params.row?.status;
        if (!status) return undefined;
        return <AssignmentStatusChip status={status} />;
      },
    },
    {
      field: 'assigned_by',
      headerName: 'Assigned By',
      width: 150,
    },
    {
      field: 'assigned_at',
      headerName: 'Assigned At',
      filterable: false,
      width: 150,
      valueFormatter: ({ value }: { value: string }) => {
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<LocationAssignment>) => {
        const assignment = params.row;
        if (!assignment) return undefined;

        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEdit(assignment.id)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {assignment.status === 'active' ? (
              <Tooltip title="Deactivate">
                <IconButton
                  size="small"
                  onClick={() => handleDeactivate(assignment.id)}
                >
                  <BlockIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Reactivate">
                <IconButton
                  size="small"
                  onClick={() => handleReactivate(assignment.id)}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Discontinue">
              <IconButton
                size="small"
                onClick={() => handleDiscontinue(assignment.id)}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(assignment.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const mutationError = createMutation.error || updateMutation.error;

  const handleCloseForm = () => {
    setIsFormOpen(false);
    createMutation.reset();
    updateMutation.reset();
  };

  // Clear assignment data only after the Dialog close animation finishes,
  // so the form content stays stable during the fade-out.
  const handleFormExited = () => {
    setSelectedAssignment(undefined);
  };

  const handleSubmit = async (data: CreateAssignmentData & {
    tagIds?: string[];
    removedTagIds?: string[];
    newTagsToCreate?: string[];
    product_equivalency_id?: number;
    group_max_count?: number;
  }) => {
    const { tagIds, removedTagIds, newTagsToCreate, product_equivalency_id, group_max_count, ...assignmentData } = data;
    createMutation.reset();
    updateMutation.reset();
    let assignmentSucceeded = false;
    const locationId = assignmentData.location_id;

    try {
      if (selectedAssignment) {
        // Update existing assignment
        await updateMutation.mutateAsync({ id: selectedAssignment.id, data: assignmentData });
        assignmentSucceeded = true;

        // Handle beacon assignments for updates with location-scoped operations
        if (tagIds?.length || removedTagIds?.length || newTagsToCreate?.length) {
          await handleLocationBeaconAssignments(
            selectedAssignment.sku,
            locationId,
            tagIds || [],
            removedTagIds || [],
            newTagsToCreate || []
          );
        }
      } else {
        // Create new assignment
        const newAssignment = await createMutation.mutateAsync(assignmentData);
        assignmentSucceeded = true;

        // Handle beacon assignments for new assignment with location-scoped operations
        if (tagIds?.length || newTagsToCreate?.length || removedTagIds?.length) {
          await handleLocationBeaconAssignments(
            newAssignment.sku,
            locationId,
            tagIds || [],
            removedTagIds || [],
            newTagsToCreate || []
          );
        }
      }

      // Save the group max to the LPE — the only max grain (sc-5845).
      // Wrapped separately so a failure here doesn't mask the successful assignment save.
      let upsertFailed = false;
      if (product_equivalency_id && group_max_count !== undefined) {
        try {
          await locationEquivalenciesApi.upsert({
            location_id: locationId,
            product_equivalency_id,
            max_count: group_max_count,
          });
        } catch (upsertError) {
          console.error('Error saving equivalency max_count:', upsertError);
          upsertFailed = true;
        }
      }

      // Invalidate queries to reflect actual database state
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
      // Also invalidate location-scoped beacon queries used by the form
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_at_location'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_available'] });

      if (upsertFailed) {
        setSnackbar({
          message: 'Assignment saved, but max count update failed. Edit the equivalency setting to retry.',
          severity: 'warning'
        });
      } else {
        setSnackbar({
          message: selectedAssignment ? 'Assignment updated successfully' : 'Assignment created successfully',
          severity: 'success'
        });
      }
      handleCloseForm();
    } catch (error) {
      console.error('Error handling assignment submission:', error);

      // Always invalidate queries to reflect actual database state
      queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_at_location'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_available'] });

      if (assignmentSucceeded) {
        // Assignment succeeded but beacon operations failed - partial success
        setSnackbar({
          message: 'Assignment saved, but Tag ID updates failed. Please try editing the assignment again.',
          severity: 'warning'
        });
        handleCloseForm();
      } else {
        // Assignment itself failed - keep form open for retry
        setSnackbar({
          message: extractApiError(error, 'Failed to save assignment. Please try again.'),
          severity: 'error'
        });
      }
    }
  };

  /**
   * Handle location-scoped beacon assignments.
   * 
   * Two-stage process:
   * 1. Stage 1 (Tag-to-Product): Create beacon assignments for new tags
   * 2. Stage 2 (Tag-to-Location): Assign/unassign beacons to/from this location
   * 
   * @param sku - The product SKU
   * @param locationId - The location ID to assign tags to
   * @param addedTagIds - Tags being added to this location (may be existing product-level or new)
   * @param removedTagIds - Tags being removed from this location (will be unassigned, not deleted)
   * @param newTagsToCreate - Tags that don't exist yet (need Stage 1 creation first)
   */
  const handleLocationBeaconAssignments = async (
    sku: string, 
    locationId: number,
    addedTagIds: string[], 
    removedTagIds: string[],
    newTagsToCreate: string[]
  ) => {
    try {
      // Stage 1: Create new beacon assignments at product level for tags that don't exist
      if (newTagsToCreate.length > 0) {
        const newAssignments = newTagsToCreate.map(beaconId => ({
          beacon_id: beaconId,
          sku,
          status: 'active' as const,
          location_id: locationId, // Assign directly to location on creation
        }));
        await beaconAssignmentsApi.bulkCreate(newAssignments);
      }
      
      // Stage 2a: Assign existing tags to this location (tags that were "pulled" from product level)
      // These are tags in addedTagIds that are NOT in newTagsToCreate (already exist at product level)
      const tagsToAssignToLocation = addedTagIds.filter(
        tagId => !newTagsToCreate.includes(tagId)
      );
      if (tagsToAssignToLocation.length > 0) {
        await beaconAssignmentsApi.bulkAssignToLocation(tagsToAssignToLocation, locationId);
      }
      
      // Stage 2b: Unassign tags from this location (they go back to product level, not deleted)
      if (removedTagIds.length > 0) {
        await beaconAssignmentsApi.bulkUnassignFromLocation(removedTagIds);
      }
      
      // Invalidate all beacon-related queries
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_at_location'] });
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments_available'] });
    } catch (error) {
      console.error('Error managing location beacon assignments:', error);
      throw error;
    }
  };

  const handleEdit = (id: number) => {
    const assignment = assignments.find((a) => a.id === id);
    if (assignment) {
      setSelectedAssignment(assignment);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeactivate = (id: number) => {
    const reason = window.prompt('Please provide a reason for deactivation:');
    if (reason !== null) {
      deactivateMutation.mutate({ id, reason });
    }
  };

  const handleReactivate = (id: number) => {
    reactivateMutation.mutate(id);
  };

  const handleDiscontinue = (id: number) => {
    const reason = window.prompt('Please provide a reason for discontinuation:');
    if (reason !== null) {
      discontinueMutation.mutate({ id, reason });
    }
  };

  const handleFilterChange = (field: keyof typeof filters) => (
    e: SelectChangeEvent<string>
  ) => {
    const value = e.target.value;
    const newFilters = {
      ...filters,
      [field]: value,
    };
    
    // Clear lower filters when higher filters change
    // Precedence: Organization > Location > Product > Status
    if (field === 'organization_id') {
      newFilters.location_id = '';
      newFilters.sku = '';
      newFilters.status = '';
    } else if (field === 'location_id') {
      // Don't clear organization - it's higher in precedence
      newFilters.sku = '';
      newFilters.status = '';
    } else if (field === 'sku') {
      // Don't clear organization or location - they're higher in precedence
      newFilters.status = '';
    }
    // Status is lowest - changing it doesn't affect other filters
    
    setFilters(newFilters);
    
    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set(field, value);
    } else {
      newSearchParams.delete(field);
    }
    
    // Clear lower URL parameters when higher filters change
    if (field === 'organization_id') {
      newSearchParams.delete('location_id');
      newSearchParams.delete('sku');
      newSearchParams.delete('status');
    } else if (field === 'location_id') {
      newSearchParams.delete('sku');
      newSearchParams.delete('status');
    } else if (field === 'sku') {
      newSearchParams.delete('status');
    }
    
    setSearchParams(newSearchParams);
  };

  if (error) {
    return <Alert severity="error">Failed to load assignments</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Location Product Assignments</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Create Assignment
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={filters.organization_id}
            onChange={handleFilterChange('organization_id')}
            label="Organization"
          >
            <MenuItem value="">All Organizations</MenuItem>
            {organizations.map((organization) => (
              <MenuItem key={organization.id} value={organization.id.toString()}>
                {organization.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={locations}
          value={locations.find(l => l.id.toString() === filters.location_id) ?? null}
          onChange={(_e, location) => {
            const value = location?.id?.toString() ?? '';
            const newFilters = { ...filters, location_id: value, sku: '', status: '' };
            setFilters(newFilters);
            const newSearchParams = new URLSearchParams(searchParams);
            if (value) {
              newSearchParams.set('location_id', value);
            } else {
              newSearchParams.delete('location_id');
            }
            newSearchParams.delete('sku');
            newSearchParams.delete('status');
            setSearchParams(newSearchParams);
          }}
          getOptionLabel={(option) => `${option.name} (${option.slug})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Location" />
          )}
          sx={{ minWidth: 250 }}
        />

        <Autocomplete
          options={availableProducts}
          value={availableProducts.find(p => p.sku === filters.sku) ?? null}
          onChange={(_e, product) => {
            const value = product?.sku ?? '';
            const newFilters = { ...filters, sku: value, status: '' };
            setFilters(newFilters);
            const newSearchParams = new URLSearchParams(searchParams);
            if (value) {
              newSearchParams.set('sku', value);
            } else {
              newSearchParams.delete('sku');
            }
            newSearchParams.delete('status');
            setSearchParams(newSearchParams);
          }}
          getOptionLabel={(option) => `${option.sku} (${option.name})`}
          isOptionEqualToValue={(option, value) => option.sku === value.sku}
          renderInput={(params) => (
            <TextField {...params} label="Product" />
          )}
          sx={{ minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            onChange={handleFilterChange('status')}
            label="Status"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="discontinued">Discontinued</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DataTable
        rows={assignments}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        pageSizeOptions={[25, 50, 100]}
        fullHeight={!filters.location_id}
        maxHeight={500}
        autoHeight={!!filters.location_id}
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
      />

      {/* Product Equivalency Settings section - shown when a location is selected */}
      {filters.location_id && (
        <>
          <Divider sx={{ my: 3 }} />
          <LocationEquivalenciesPanel
            locationId={parseInt(filters.location_id, 10)}
            locationName={
              locations.find((l) => l.id.toString() === filters.location_id)?.name || 'Selected Location'
            }
          />
        </>
      )}

      <LocationAssignmentForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onExited={handleFormExited}
        onSubmit={handleSubmit}
        assignment={selectedAssignment}
        initialOrganizationId={
          filters.organization_id ||
          (filters.location_id
            ? locations.find(l => l.id.toString() === filters.location_id)?.organization_id?.toString()
            : undefined)
        }
        initialLocationId={filters.location_id || undefined}
        isLoading={createMutation.isPending || updateMutation.isPending}
        error={mutationError ? extractApiError(mutationError) : undefined}
      />

      {snackbar && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}; 